import { Box } from '@osmosis/tailwind-ui/components/Box';
import { A, H4 } from '@osmosis/tailwind-ui/components/Text';
import React, { useEffect } from 'react';
import {
  PlaylistProps,
  PlaylistItemProps,
  PlaylistLearnNodeItem,
} from '@osmosis/shared/types/playlist';

import PlaylistVideosHeader from './PlaylistVideosHeader';
import Divider from '../../../components/Divider';
import PlaylistEmpty from './PlaylistEmpty';
import PlaylistItemRow from './PlaylistItemRow';
import PlaylistSearch from './search/PlaylistSearch';
import useModal from '../../../hooks/useModal';
import { Reorder } from 'motion/react';
import { usePlaylistContext } from '../../../context/PlaylistProvider';
import { useSnackbarContext } from '../../../context/SnackbarProvider';
import { useUserContext } from '../../../context/UserContext';

import { FormattedMessage } from 'react-intl';
import { nameToLearnUrl } from '@osmosis/shared/video';
import { bgWarningLight, textWarningText } from '@osmosis/tailwind-ui/styles';

type Props = {
  playlist: PlaylistProps;
  retireFeatureFlag: boolean;
};

/**
 * Returns header with playlist info
 * Renders video items (working with mock videos data yet).
 */
export const PlaylistVideos = (props: Props): JSX.Element => {
  const { playlist, retireFeatureFlag } = props;

  const playlistSearchModal = useModal();
  const { removePlaylistVideos, reorderItems } = usePlaylistContext();
  const { onAddSnackBar } = useSnackbarContext();
  const { user, getPlansUrl } = useUserContext();
  const isLockedFlag = user?.hasLockedContentFlag;

  const isPlaylistMine = playlist.userID === user?.userID && playlist.userOwned;

  // Save local videos for faster updating
  const [videos, setVideos] = React.useState(playlist?.list || []);

  const handleRemove = async (items: PlaylistLearnNodeItem[]) => {
    await removePlaylistVideos({
      items,
      playlistName: playlist.title,
      playlistUrl: playlist.url,
      onAddSnackBar,
    });
    playlist.list = playlist.list.filter(
      (item) => !items.find((i) => i.learnNodeID === item.data.nodeID),
    );
    setModalOpen(false);
  };

  const reorderHandler = async (orderedList: PlaylistItemProps[]) => {
    setVideos(orderedList);
    await reorderItems(
      orderedList.map((i) => i.data.nodeID),
      playlist.url,
    );
  };

  const moveItemAmount = (nodeID: number, moveAmount: number) =>
    moveItemAmountExt(nodeID, moveAmount, videos, reorderHandler);

  useEffect(() => {
    if (playlist?.list) {
      setVideos(playlist.list);
    }
  }, [playlist.list]);

  const anyLockedContent = videos?.filter((video) => {
    if (video.accessState) {
      const accessStateValues = Object.values(video.accessState);
      if (accessStateValues.length === 1) {
        return accessStateValues.includes('locked');
      }
    }
  });

  let learnUrl;
  const [firstVideo] = videos;
  if (firstVideo?.title) {
    learnUrl = nameToLearnUrl(firstVideo.title);
  }

  const [modalOpen, setModalOpen] = React.useState(false);

  return (
    <Box className="rounded-[16px] border border-solid border-gray-200">
      <PlaylistVideosHeader
        learnUrl={learnUrl}
        playlist={playlist}
        searchToggle={playlistSearchModal.toggle}
      />

      <Divider />

      {isLockedFlag && anyLockedContent.length ? (
        <Box
          data-testid="subscription-options-banner"
          className={`p-2 lg:p-0 ${bgWarningLight}`}
        >
          <H4 className={`py-1 pl-2 lg:py-2 lg:pl-8 ${textWarningText}`}>
            <FormattedMessage
              id="playlist.see.subscription.options.description"
              defaultMessage="This playlist contains videos that aren't included in your current subscription."
            />
            <A
              href={getPlansUrl()}
              className={`pl-2 underline ${textWarningText}`}
            >
              <FormattedMessage
                id="playlist.see.subscription.options.link"
                defaultMessage="See subscription options"
              />
            </A>
          </H4>
        </Box>
      ) : null}

      <Box className={`relative z-0`}>
        <PlaylistSearch
          playlist={playlist}
          {...playlistSearchModal}
          handleRemove={handleRemove}
        />

        {videos?.length ? (
          <Box data-testid="playlist-item-row-container" className={`p-8`}>
            <Reorder.Group
              values={videos}
              onReorder={(orderedList) => reorderHandler(orderedList)}
              className="m-0 list-none p-0"
            >
              {videos.map((item, index) => (
                <PlaylistItemRow
                  key={item.title}
                  playlistItem={item}
                  isPlaylistMine={isPlaylistMine}
                  playlistUrl={playlist.url}
                  onRemove={handleRemove}
                  moveItemAmount={moveItemAmount}
                  rowNumber={index}
                  maxRows={videos.length}
                  modalOpen={modalOpen}
                  setModalOpen={setModalOpen}
                  retiredFeatureFlag={retireFeatureFlag}
                  searchToggle={playlistSearchModal.toggle}
                />
              ))}
            </Reorder.Group>
          </Box>
        ) : (
          <PlaylistEmpty
            playlist={playlist}
            searchToggle={playlistSearchModal.toggle}
          />
        )}
      </Box>
    </Box>
  );
};

/**
 * Calculate new index for an item and reorder the list.
 * @param nodeID The id of the item to move.
 * @param moveAmount How many positions to move.
 * @param videos Parent list of items.
 * @param reorderHandler Function to reorder the list.
 */
export function moveItemAmountExt(
  nodeID: number,
  moveAmount: number,
  videos: PlaylistItemProps[],
  reorderHandler: (arg0: PlaylistItemProps[]) => void,
) {
  if (nodeID >= 0 && moveAmount) {
    const index = videos.findIndex((item) => item.data.nodeID === nodeID);
    if (
      index >= 0 &&
      index + moveAmount < videos.length &&
      index + moveAmount >= 0
    ) {
      const newVideos = [...videos];
      [newVideos[index + moveAmount], newVideos[index]] = [
        newVideos[index],
        newVideos[index + moveAmount],
      ];

      reorderHandler(newVideos);
    }
  }
}

export default PlaylistVideos;
